import React, { useState, useEffect } from 'react';
import { Box, Button, TextField, Paper, Dialog, DialogTitle, DialogContent, DialogActions, Alert } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { apiGet, apiRequest } from '../../../utils/fetch';
import { UserSessionInterface, GenericResponseInterface } from '../../../interfaces/interfaces';
import { getUserSessionsTableColumns } from './UserSessionsTableColumns';

const UserSessionsAdminList: React.FC = () => {
    const [sessions, setSessions] = useState<UserSessionInterface[]>([]);
    const [session, setSession] = useState<UserSessionInterface | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [sessionId, setSessionId] = useState('');
    const [userId, setUserId] = useState('');
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [sessionToDelete, setSessionToDelete] = useState<UserSessionInterface | null>(null);
    const [toggleActiveDialogOpen, setToggleActiveDialogOpen] = useState(false);
    const [sessionToToggleActive, setSessionToToggleActive] = useState<UserSessionInterface | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    // Fetch all sessions
    const handleGetAllSessions = async () => {
        setError(null);
        setSession(null);
        setSuccess(null);
        try {
            const result = await apiGet<GenericResponseInterface<UserSessionInterface[]>>('/userSessions');
            if (result.status && result.data) {
                setSessions(result.data);
            } else {
                setError(result.message || 'Failed to fetch sessions');
            }
        } catch (err: any) {
            setError(err.message || 'Failed to fetch sessions');
        }
    };

    // Fetch session by session id
    const handleGetSessionById = async () => {
        setError(null);
        setSessions([]);
        setSuccess(null);
        if (!sessionId) return setError('Please enter a session ID');
        try {
            const result = await apiGet<GenericResponseInterface<UserSessionInterface>>(`/userSessions/${sessionId}`);
            if (result.status && result.data) {
                setSession(result.data);
            } else {
                setError(result.message || 'Failed to fetch session');
            }
        } catch (err: any) {
            setError(err.message || 'Failed to fetch session');
        }
    };

    // Fetch sessions by user id
    const handleGetSessionsByUserId = async () => {
        setError(null);
        setSession(null);
        setSuccess(null);
        if (!userId) return setError('Please enter a user ID');
        try {
            const result = await apiGet<GenericResponseInterface<UserSessionInterface[]>>(`/userSessions/userId/${userId}`);
            if (result.status && result.data) {
                setSessions(result.data);
            } else {
                setError(result.message || 'Failed to fetch sessions');
            }
        } catch (err: any) {
            setError(err.message || 'Failed to fetch sessions');
        }
    };

    // Delete session
    const handleDeleteSession = async () => {
    if (!sessionToDelete) return;
        setError(null);
        setSuccess(null);
        try {
            const result = await apiRequest<GenericResponseInterface>(`/userSessions/delete/${sessionToDelete.id}`, 'DELETE');
            if (result.status) {
                setSuccess(result.message || 'Session deleted successfully');
                setDeleteDialogOpen(false);
                setSessionToDelete(null);
                handleGetAllSessions();
            } else {
                setError(result.message || 'Failed to delete session');
            }
        } catch (err: any) {
            setError(err.message || 'Failed to delete session');
        }
    };

    // Toggle active (future backend)
    const handleToggleActive = async () => {
    if (!sessionToToggleActive) return;
        setError(null);
        setSuccess(null);
        try {
            const result = await apiRequest<GenericResponseInterface>(`/userSessions/toggleActive/${sessionToToggleActive.id}`, 'PUT');
            if (result.status) {
                setSuccess(result.message || 'Active status toggled successfully');
                setToggleActiveDialogOpen(false);
                setSessionToToggleActive(null);
                handleGetAllSessions();
            } else {
                setError(result.message || 'Failed to toggle active status');
            }
        } catch (err: any) {
            setError(err.message || 'Failed to toggle active status');
        }
    };

    const columns = getUserSessionsTableColumns(
        setSessionToDelete,
        setDeleteDialogOpen,
        setSessionToToggleActive,
        setToggleActiveDialogOpen
    );

    useEffect(() => {
        handleGetAllSessions();
    }, []);

    return (
    <Box>
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <Button variant="contained" onClick={handleGetAllSessions}>Get All Sessions</Button>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <Button variant="contained" onClick={handleGetSessionById}>Get Session by ID</Button>
            <TextField
                label="Session ID"
                value={sessionId}
                onChange={e => setSessionId(e.target.value)}
                size="small"
            />
        </Box>
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <Button variant="contained" onClick={handleGetSessionsByUserId}>Get Sessions by User ID</Button>
            <TextField
                label="User ID"
                value={userId}
                onChange={e => setUserId(e.target.value)}
                size="small"
            />
        </Box>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
        <Paper sx={{ width: '100%', overflow: 'auto', mt: 2 }}>
        {sessions.length > 0 && (
            <DataGrid
            rows={sessions.map(s => ({ ...s, id: s.id ?? Math.random() }))}
            columns={columns}
            initialState={{
                pagination: { paginationModel: { pageSize: 10, page: 0 } }
            }}
            pageSizeOptions={[10, 25, 50]}
            autoHeight
            />
        )}
        {session && (
            <DataGrid
            rows={[{ ...session, id: session.id ?? Math.random() }]}
            columns={columns}
            hideFooter
            autoHeight
            />
        )}
        </Paper>

        {/* Delete confirmation dialog */}
        <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
            Are you sure you want to delete this session? Maybe just Toggle Active instead?
        </DialogContent>
        <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button color="error" onClick={handleDeleteSession}>Delete</Button>
        </DialogActions>
        </Dialog>

        {/* Toggle active dialog (future) */}
        <Dialog open={toggleActiveDialogOpen} onClose={() => setToggleActiveDialogOpen(false)}>
        <DialogTitle>Toggle Active</DialogTitle>
        <DialogContent>
            Are you sure you want to toggle the active status on this session? User will be kicked out but can log in again. If the purpose is to disallow user to login again use the "administrate Users" and deactivate the account.  
        </DialogContent>
        <DialogActions>
            <Button onClick={() => setToggleActiveDialogOpen(false)}>Cancel</Button>
            <Button color="warning" onClick={handleToggleActive}>Toggle</Button>
        </DialogActions>
        </Dialog>
    </Box>
    );
};

export default UserSessionsAdminList;